// Everything the app does, in one place: the Features page, the header menu, the footer and
// the waitlist interests all read from here.
//   short      one sentence: what it is
//   how        the steps a person takes
//   notes      the details worth knowing
//   where      where it lives in the app (All services groups and tiles, as the app labels them)
//   android    true when it works on Android only
// Keep every word to what the app actually ships (backend/docs/*.md for each feature, and
// the release on the app's main branch). Round-ups and auto-save are NOT live: see COMING_SOON.
import {
    ArrowLeftRight,
    AudioLines,
    BadgeCheck,
    BellRing,
    CalendarClock,
    CircleCheck,
    CirclePlus,
    CreditCard,
    Fingerprint,
    Gamepad2,
    Gift,
    Goal,
    HandCoins,
    History,
    Languages,
    Lightbulb,
    LineChart,
    MessageSquareText,
    Moon,
    Receipt,
    Rocket,
    ScanLine,
    ScrollText,
    Send,
    ShoppingBag,
    Smartphone,
    Sparkles,
    Split,
    TrainFront,
    Trophy,
    Truck,
    UsersRound,
    WalletCards,
} from "lucide-react";

export const FEATURE_GROUPS = [
    {
        id: "track",
        label: "Track",
        title: "Every spend, sorted on its own",
        intro: "Bank alerts become a clean, categorised record, with insights and a nightly summary that tell you what changed.",
    },
    {
        id: "split",
        label: "Split",
        title: "Split bills without the awkward maths",
        intro: "Share a dinner, a trip or the rent with friends, see who owes whom, and settle up in the app.",
    },
    {
        id: "pay",
        label: "Pay",
        title: "Recharges, bills and transfers in one place",
        intro: "Pay from your Accruewise Wallet. The full amount is shown before you approve, and every payment asks for your MPIN or fingerprint.",
    },
    {
        id: "save",
        label: "Save",
        title: "Plan for the thing, not for “someday”",
        intro: "Goals turn a vague plan into a number, a date and an amount to put aside each week.",
    },
    {
        id: "card",
        label: "Wallet & card",
        title: "A wallet and a RuPay card in your pocket",
        intro: "Open the wallet with a quick KYC, then pay online, in store and on the metro.",
    },
    {
        id: "rewards",
        label: "Rewards",
        title: "Small wins along the way",
        intro: "Scratch cards, gift vouchers and games, with nothing pretending to be cash.",
    },
    {
        id: "assistant",
        label: "Assistant",
        title: "Ask your money a question",
        intro: "Type or talk to the Accruewise Assistant in English or Hindi. It asks before it acts.",
    },
    {
        id: "security",
        label: "Security & control",
        title: "Locked down, and in your control",
        intro: "Your MPIN or fingerprint guards the app and every payment, and you choose what we notify you about.",
    },
];

export const FEATURES = [
    // ---- Track
    {
        id: "spends",
        group: "track",
        Icon: MessageSquareText,
        title: "Automatic spend tracking",
        short: "Bank, card and UPI alerts become entries by themselves. Reminders and repeat alerts are skipped.",
        how: ["Allow SMS access once", "Accruewise reads alerts from bank, card and UPI sender IDs only", "Each payment is filed with its amount, payee, category and time"],
        notes: [
            "Reminders, “will be debited” notices, failed payments, OTPs and repeat alerts are skipped",
            "Turn SMS reading off any time from the tracker's ⋮ menu",
            "A deleted entry doesn't come back on the next sync",
            "The first sync looks back 90 days",
        ],
        where: "All services → Spend tracker",
        android: true,
    },
    {
        id: "paste-sms",
        group: "track",
        Icon: ScanLine,
        title: "Paste a bank SMS",
        short: "On iPhone, copy a bank alert and paste it: the amount, payee, category and date fill in for you.",
        how: ["Copy a bank alert from Messages", "Tap Paste bank SMS on the Add screen", "Check what filled in, then save"],
        notes: ["Works on Android too"],
        where: "Spend tracker → Add",
    },
    {
        id: "quick-add",
        group: "track",
        Icon: CirclePlus,
        title: "Add a spend in seconds",
        short: "Type the amount and tap Save. The category you use most is already picked.",
        how: ["Tap Add on the tracker", "Type the amount, or work it out with the calculator", "Tap Save"],
        notes: [
            "Swipe the amount sideways to switch between a spend and income",
            "Add who you paid, a note, tags or a photo of the receipt only when you want to",
        ],
        where: "Spend tracker → Add",
        badge: "New",
    },
    {
        id: "insights",
        group: "track",
        Icon: LineChart,
        title: "Insights that explain",
        short: "This month against last, where the money went, your busiest day and the payments that repeat.",
        how: ["Open Insights for any month or range", "Read the tips first: what changed and why", "Then your pace, categories, top payees and recurring payments"],
        notes: ["Compared with the previous period", "Recurring payments are spotted from the last 120 days", "Export any period as a spreadsheet (CSV)"],
        where: "Spend tracker → Insights",
    },
    {
        id: "budgets",
        group: "track",
        Icon: CalendarClock,
        title: "Budgets with a daily limit",
        short: "Set a monthly budget and see how much is safe to spend each day to stay within it.",
        how: ["Set a monthly amount, or pick your own dates", "See what's safe to spend each day", "Get a projection of where the month will land"],
        notes: ["A heads-up when you've spent 80% of it, and again at 100%", "A budget is optional: the tracker works without one"],
        where: "Spend tracker → Overview",
    },
    {
        id: "daily-summary",
        group: "track",
        Icon: Moon,
        title: "A 9 PM spend summary",
        short: "Each evening, one notification with what you spent today and this month so far.",
        how: ["It arrives at 9 PM", "Tap Review to see the day's entries"],
        notes: ["A recap of the month on the 1st", "Switch it off under notification settings → Summaries"],
        badge: "New",
    },
    {
        id: "suggestions",
        group: "track",
        Icon: Lightbulb,
        title: "A next step on every transaction",
        short: "Open an entry to see what you can do with it: split it, set a budget, or a live voucher or coupon for that brand.",
        how: ["Open any entry in Transactions", "See one or two suggestions based on it", "Tap one to act on it"],
        notes: ["Offers appear only while they're live; nothing is made up", "New entries read from SMS also get a short notification"],
        where: "Spend tracker → Transactions",
        badge: "New",
    },

    // ---- Split
    {
        id: "splits",
        group: "split",
        Icon: Split,
        title: "Split bills with friends",
        short: "Split a dinner, a trip or the rent with one friend or a group, equally or your way.",
        how: [
            "Add an expense, or tap Split this on a spend, a bill or a voucher",
            "Choose a group or friends, then split equally, by exact amounts, percentages or shares",
            "Everyone sees who owes whom, worked out in the fewest payments",
        ],
        notes: [
            "Add friends who aren't on Accruewise by phone number, and share the invite link yourself",
            "Their balances are waiting for them when they sign up with that number",
            "A group can be deleted only by its admin, and only once everyone is settled",
        ],
        where: "All services → Split bills",
        badge: "New",
    },
    {
        id: "split-from-spends",
        group: "split",
        Icon: HandCoins,
        title: "Split without typing",
        short: "Spends you're likely to share are ready to split: one tap fills in the amount, payee and date.",
        how: ["Open Split bills", "Pick a spend under “Split a recent spend”", "Your last group is already chosen; confirm the split"],
        notes: ["Spends of ₹100 or more from the last three weeks are suggested", "Eating out, travel, movies and groceries come first"],
        where: "Split bills → Split a recent spend",
        badge: "New",
    },
    {
        id: "settle-up",
        group: "split",
        Icon: CircleCheck,
        title: "Settle up your way",
        short: "Pay a friend back from your Accruewise Wallet, with any UPI app, or mark a cash payment as done.",
        how: ["Open a friend or a group and tap Settle up", "Pay from your wallet with your MPIN or fingerprint, or open your UPI app", "Paid in cash? Mark it paid"],
        notes: [
            "Wallet payments go to friends who have an Accruewise Wallet, and can't be undone",
            "UPI and cash records can be undone by either of you",
            "Friends who owe get a gentle weekly reminder, and you can send one yourself",
        ],
        where: "Split bills → a friend or group",
        badge: "New",
    },

    // ---- Pay
    {
        id: "recharge",
        group: "pay",
        Icon: Smartphone,
        title: "Mobile & DTH recharge",
        short: "Browse plans by operator and circle and recharge in a few taps.",
        how: ["Enter the number; the operator and circle fill in once you've recharged it before", "Pick a plan: your last plan and the best value per day are suggested", "Approve with your MPIN or fingerprint"],
        notes: ["A reminder before your plan runs out", "A failed recharge is refunded to your wallet on its own"],
        where: "All services → Mobile recharge or DTH Recharge",
    },
    {
        id: "bills",
        group: "pay",
        Icon: Receipt,
        title: "Bill payments (BBPS)",
        short: "Electricity, broadband, gas, water and more through Bharat BillPay, with a reminder before the due date.",
        how: ["Pick a category and your biller", "Enter your consumer number and fetch the bill", "See the total, including any fee, and pay with your MPIN or fingerprint"],
        notes: ["Also postpaid, FASTag, loan EMIs and insurance", "An invoice for every successful payment", "A failed payment is refunded to your wallet on its own"],
        where: "All services → Pay Bills",
    },
    {
        id: "wallet-send",
        group: "pay",
        Icon: Send,
        title: "Send to an Accruewise Wallet",
        short: "Send money to someone's Accruewise Wallet with just their phone number.",
        how: ["Tap Send on your wallet", "Enter their number: you see their name and the last four digits to check it's them", "Confirm with your MPIN or fingerprint"],
        notes: ["Less than ₹2,00,000 at a time", "You both get a notification", "People you've sent to before are one tap away"],
        where: "Accruewise Wallet → Send",
        badge: "New",
    },
    {
        id: "transfers",
        group: "pay",
        Icon: ArrowLeftRight,
        title: "Pay to bank & UPI",
        short: "Send money to a bank account you've saved by IMPS, or pay a UPI ID, confirmed with an OTP.",
        how: ["Save the bank account once (this asks for your MPIN)", "Pick it and enter an amount", "Confirm with the OTP sent to your mobile"],
        notes: ["Transfers go only to accounts you've saved", "The receipt updates its own status, and you can share it"],
        where: "All services → Pay to bank & UPI",
    },
    {
        id: "ncmc",
        group: "pay",
        Icon: TrainFront,
        title: "NCMC transit top-up",
        short: "Top up your National Common Mobility Card for metro gates and buses from your wallet.",
        how: ["Open NCMC Card", "Enter an amount", "Approve with your MPIN or fingerprint, and the card's balance updates"],
        notes: ["Tap the card at metro gates, buses and other NCMC readers"],
        where: "All services → NCMC Card",
    },

    // ---- Save
    {
        id: "goals",
        group: "save",
        Icon: Goal,
        title: "Savings goals",
        short: "Set a goal for a trip, a gadget or an emergency fund, with the amount and the date you want it by.",
        how: ["Pick a category, like travel, shopping, gadgets or sports kit, or make your own", "Set the amount and the date", "See how much to put aside each week to get there"],
        notes: ["Change the amount or the date whenever plans change"],
        where: "Home → My Goals",
    },
    {
        id: "goal-boosts",
        group: "save",
        Icon: Rocket,
        title: "Reach it faster",
        short: "Every goal suggests real ways to get there sooner, from a weekly amount to a category you could spend less on.",
        how: ["Open a goal", "Scroll to “Reach it faster”", "Tap a suggestion to act on it"],
        notes: [
            "Built from your own spends and offers that are live right now, such as scratch cards or a gift-voucher discount",
            "Rent, EMIs and bills are never suggested as cuts",
            "Nothing to suggest? The section stays hidden",
        ],
        where: "A goal → Reach it faster",
        badge: "New",
    },
    {
        id: "marketplace",
        group: "save",
        Icon: ShoppingBag,
        title: "Goals for real products",
        short: "Pick a product in the Marketplace and its price becomes your goal.",
        how: ["Browse products in the Marketplace", "Add one to a goal: its price becomes the target", "Plan for it like any other goal"],
        notes: ["Buying inside the app isn't open yet; spend the money with your wallet or card"],
        to: "/marketplace",
    },

    // ---- Wallet & card
    {
        id: "wallet",
        group: "card",
        Icon: WalletCards,
        title: "Accruewise Wallet",
        short: "Powered by ToPay. One balance for bills, recharges, transfers and the card, with a passbook of every transaction.",
        how: ["Open the wallet with a quick KYC", "Add money by bank transfer or any UPI app; the Full KYC upgrade unlocks the account number and UPI ID if they aren't there yet", "Pay, send and spend from the same balance"],
        notes: ["Holds up to ₹2,00,000", "Adding money by UPI needs the upgraded KYC"],
        where: "All services → Accruewise Wallet",
    },
    {
        id: "kyc",
        group: "card",
        Icon: BadgeCheck,
        title: "Two-step KYC",
        short: "Open your wallet with Aadhaar on DigiLocker and a few details. Upgrade your limit up to ₹2L when you need to.",
        how: ["Verify Aadhaar through DigiLocker", "Confirm a few details about you", "When you're ready, upgrade with your PAN and a short video call"],
        notes: ["The upgrade raises your limit up to ₹2L and adds UPI", "“Maybe later” is always an option"],
        where: "Accruewise Wallet → Upgrade",
        badge: "New",
    },
    {
        id: "card",
        group: "card",
        Icon: CreditCard,
        title: "Prepaid RuPay card",
        short: "A virtual RuPay card comes with your wallet, for online and in-store payments.",
        how: ["Activate your wallet, and the virtual card is ready", "See the card details when you need them", "Pay online or in store wherever RuPay is accepted"],
        notes: [
            "Switch online, in-store, contactless, ATM and international use on or off",
            "Block the card, or set its PIN, from Card settings",
            "Your virtual and physical cards sit side by side",
        ],
        where: "All services → Prepaid card",
        to: "/card",
    },
    {
        id: "physical-card",
        group: "card",
        Icon: Truck,
        title: "A physical card, delivered",
        short: "Order a physical RuPay card to one of your saved addresses.",
        how: ["Open Card settings and tap Order physical card", "Pick a saved address, or add one (the PIN code fills the city and state)", "Confirm with your MPIN or fingerprint"],
        notes: ["One working physical card at a time; a blocked one can be replaced", "Set its PIN from Card settings"],
        where: "Prepaid card → Card settings",
        badge: "New",
    },

    // ---- Rewards
    {
        id: "scratch",
        group: "rewards",
        Icon: Sparkles,
        title: "Scratch cards & coupons",
        short: "Paying a bill or recharge, buying a voucher, winning a game or reaching a spend-tracker milestone can unlock a scratch card. Some reveal a brand coupon; offers you can claim live in the same place.",
        how: ["Pay a bill or recharge, send money, buy a voucher, win a game or hit a tracker milestone", "A scratch card lands in Coupons & scratch cards", "Scratch it: some hide a brand coupon, some say better luck next time"],
        notes: ["Cards come from the rewards running at the time", "Scratch cards hold coupons, never coins or cash", "Each coupon has its own terms and expiry"],
        where: "All services → Coupons & scratch cards",
    },
    {
        id: "vouchers",
        group: "rewards",
        Icon: Gift,
        title: "Gift vouchers",
        short: "Buy gift vouchers from popular brands straight from your wallet, for yourself or to gift.",
        how: ["Pick a brand, a value and how many", "Pay from your wallet with your MPIN or fingerprint", "The voucher appears in My vouchers"],
        notes: ["If a voucher can't be issued, the money goes back to your wallet"],
        where: "All services → Gift vouchers",
    },
    {
        id: "games",
        group: "rewards",
        Icon: Gamepad2,
        title: "Spin & Win and games",
        short: "Quick games that earn Accruewise Coins and XP. Coins have no cash value.",
        how: ["Open Spin & Win", "Play, check in each day, and finish missions for XP"],
        notes: ["Accruewise Coins have no cash value"],
        where: "All services → Spin & Win",
    },
    {
        id: "leaderboard",
        group: "rewards",
        Icon: Trophy,
        title: "Weekly leaderboard",
        short: "See where you rank by XP each week. It rewards playing, not luck, and it's just for fun.",
        how: ["Open Spin & Win", "Your place shows under your player card", "Tap it for this week's board or all time"],
        notes: ["No prizes", "Shown as your first name and initial, or hide your name completely", "The weekly board resets every Monday"],
        where: "Spin & Win → Leaderboard",
        badge: "New",
    },
    {
        id: "refer",
        group: "rewards",
        Icon: UsersRound,
        title: "Invite friends",
        short: "Share Accruewise with friends and family in a tap, so they can start too.",
        how: ["Tap Invite friends", "Share your link on WhatsApp or anywhere else"],
        where: "All services → Invite friends",
    },

    // ---- Assistant
    {
        id: "assistant",
        group: "assistant",
        Icon: AudioLines,
        title: "Accruewise Assistant",
        short: "“How much did I spend on food this month?” or “Split last night's dinner with the Goa group”. Type or speak, in English or Hindi.",
        how: ["Type, or tap the mic and talk, in English or Hindi", "It answers from your own goals, spends, splits and wallet", "Anything that changes shows a confirmation card first"],
        notes: [
            "Payments, and paying a friend back from your wallet, wait for your MPIN or fingerprint",
            "Bank transfers open the payment screen filled in; you confirm with an OTP",
            "It won't recommend other apps, stocks or loans",
        ],
        where: "All services → Accruewise Assistant",
        to: "/ai-agents",
    },
    {
        id: "assistant-habits",
        group: "assistant",
        Icon: History,
        title: "It learns your usual",
        short: "Your regular recharges, bills and payees become one-tap suggestions, just for you.",
        how: ["Use the Assistant as you normally would", "Your usual recharges, bills and payees appear as quick chips", "Tap one to repeat it, still with your approval"],
        notes: ["Learned from your own activity only, and never shown to anyone else", "Rate an answer up or down to help the team improve it"],
        badge: "New",
    },

    // ---- Security & control
    {
        id: "security",
        group: "security",
        Icon: Fingerprint,
        title: "MPIN & biometric lock",
        short: "A 6-digit MPIN, or Face ID or fingerprint, unlocks the app and confirms every payment.",
        notes: ["Easy PINs like 123456 are refused", "It locks after two minutes in the background", "Five wrong tries lock it for 15 minutes"],
    },
    {
        id: "notifications",
        group: "security",
        Icon: BellRing,
        title: "Notifications you control",
        short: "A switch for each kind of notification, and no more than two friendly reminders a day.",
        how: ["Open notification settings", "Switch off any kind you don't want: summaries, goals, rewards or reminders"],
        notes: ["Payment and security alerts always come through", "While the app is open, alerts show inside it instead of as a phone banner"],
        badge: "New",
    },
    {
        id: "languages",
        group: "security",
        Icon: Languages,
        title: "English & हिंदी",
        short: "Use the whole app in English or Hindi, and switch any time from your profile.",
        notes: ["The Assistant understands both"],
        where: "Profile → Language",
    },
    {
        id: "privacy",
        group: "security",
        Icon: ScrollText,
        title: "Clear policies",
        short: "Plain-language privacy, refund and grievance policies, and account deletion when you ask for it.",
        notes: ["Delete your account from Profile → About Accruewise → Delete account"],
    },
];

export const featuresIn = (group) => FEATURES.filter((f) => f.group === group);

// On the way, not live. Say "coming soon" only, with no figures (see the investing roadmap).
export const COMING_SOON = [
    {
        group: "save",
        title: "Round-ups and auto-save",
        text: "Spare change from everyday payments, and automatic amounts on a schedule, moving into your goals.",
    },
];

// Options for "What are you most excited about?" on the waitlist form. Values must match
// the backend's allowed interests.
export const WAITLIST_INTERESTS = [
    { value: "goals", label: "Savings goals" },
    { value: "spends", label: "Spend tracker" },
    { value: "payments", label: "Bills & recharges" },
    { value: "card", label: "RuPay card" },
    { value: "rewards", label: "Rewards" },
    { value: "assistant", label: "AI assistant" },
    { value: "vouchers", label: "Gift vouchers" },
    { value: "ncmc", label: "NCMC top-up" },
];
